/**
 * Headless Config Types
 * Raw metafield format from Shopify app panel
 *
 * Config stored in Shop Metafield: analyzify_app_metafield_export.headless_config
 */

/**
 * Metafield value types as stored in Shopify
 */
export type MetafieldType = 'boolean' | 'single_line_text_field' | 'number_integer';

/**
 * Individual metafield value structure
 */
export interface MetafieldValue {
  type: MetafieldType;
  value: string;
}

/**
 * Raw headless config metafield format
 * Keys follow pattern: "analyzify.{key}" or "analyzify_secondary.{key}"
 *
 * @example
 * {
 *   "analyzify.ga4_status": { "type": "boolean", "value": "true" },
 *   "analyzify.ga4_id": { "type": "single_line_text_field", "value": "G-XXXXXXX" },
 *   "analyzify_secondary.ga4_id": { "type": "single_line_text_field", "value": "G-YYYYYYY" }
 * }
 */
export type HeadlessMetafield = Record<string, MetafieldValue>;

/**
 * Event abbreviation mapping for config keys
 * Used in platform event configs like ga4_events_atc, meta_events_vi, etc.
 */
export const EVENT_ABBREVIATIONS = {
  atc: 'add_to_cart',
  bc: 'begin_checkout',
  vi: 'view_item',
  vil: 'view_item_list',
  vc: 'view_cart',
  rfc: 'remove_from_cart',
  atw: 'add_to_wishlist',
  si: 'select_item',
  sch: 'search',
  gl: 'generate_lead',
  vc_change: 'variant_change',
  hp: 'homepage',
  purchase: 'purchase',
} as const;

export type EventAbbreviation = keyof typeof EVENT_ABBREVIATIONS;
export type EventName = (typeof EVENT_ABBREVIATIONS)[EventAbbreviation];

/**
 * Platform prefixes in config keys
 */
export const PLATFORM_PREFIXES = {
  ga4: 'GA4',
  gads: 'GADS',
  meta: 'FACEBOOK',
  tiktok: 'TIKTOK',
  pinterest: 'PINTEREST',
  bing: 'BING',
  snapchat: 'SNAPCHAT',
  klaviyo: 'KLAVIYO',
  criteo: 'CRITEO',
  twitter: 'X',
  clarity: 'CLARITY',
  hotjar: 'HOTJAR',
  gtm: 'GTM',
  server_side: 'SERVERSIDE',
} as const;

export type PlatformPrefix = keyof typeof PLATFORM_PREFIXES;
export type PlatformName = (typeof PLATFORM_PREFIXES)[PlatformPrefix];

/**
 * Config key namespaces
 */
export const CONFIG_NAMESPACES = {
  PRIMARY: 'analyzify',
  SECONDARY: 'analyzify_secondary',
} as const;
